import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Loader2, Shield } from 'lucide-react';
import { api, extractErrorMessage } from '../../../lib/api';
import { useTenantSettings } from '../../../contexts/TenantSettingsContext';

/**
 * Onglet "Sécurité" — US-ADM-05.
 *
 * 3 sous-sections (mot de passe, sessions, verrouillage) — chaque change
 * provoque une PATCH section partielle (deep-merged côté backend).
 */
export function SecurityPolicyTab() {
  const { settings, refresh } = useTenantSettings();
  const [minLength, setMinLength] = useState(8);
  const [requireUpper, setRequireUpper] = useState(true);
  const [requireLower, setRequireLower] = useState(true);
  const [requireDigit, setRequireDigit] = useState(true);
  const [requireSpecial, setRequireSpecial] = useState(true);
  const [historyCount, setHistoryCount] = useState(5);
  const [maxAgeDays, setMaxAgeDays] = useState(0);

  const [idleMinutes, setIdleMinutes] = useState(480);
  const [maxSessionHours, setMaxSessionHours] = useState(24);

  const [maxAttempts, setMaxAttempts] = useState(5);
  const [lockoutMinutes, setLockoutMinutes] = useState(15);
  const [notifyAdmins, setNotifyAdmins] = useState(true);

  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!settings) return;
    const s = settings.security;
    setMinLength(s.password_policy.min_length);
    setRequireUpper(s.password_policy.require_uppercase);
    setRequireLower(s.password_policy.require_lowercase);
    setRequireDigit(s.password_policy.require_digit);
    setRequireSpecial(s.password_policy.require_special);
    setHistoryCount(s.password_policy.history_count);
    setMaxAgeDays(s.password_policy.max_age_days);
    setIdleMinutes(s.session_policy.idle_timeout_minutes);
    setMaxSessionHours(s.session_policy.max_session_hours);
    setMaxAttempts(s.lockout_policy.max_failed_attempts);
    setLockoutMinutes(s.lockout_policy.lockout_duration_minutes);
    setNotifyAdmins(s.lockout_policy.notify_admins_on_lockout);
  }, [settings]);

  if (!settings) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErrorMessage(null);
    try {
      await api.patch('/admin/tenant/security', {
        password_policy: {
          min_length: minLength,
          require_uppercase: requireUpper,
          require_lowercase: requireLower,
          require_digit: requireDigit,
          require_special: requireSpecial,
          history_count: historyCount,
          max_age_days: maxAgeDays,
        },
        session_policy: {
          idle_timeout_minutes: idleMinutes,
          max_session_hours: maxSessionHours,
        },
        lockout_policy: {
          max_failed_attempts: maxAttempts,
          lockout_duration_minutes: lockoutMinutes,
          notify_admins_on_lockout: notifyAdmins,
        },
      });
      await refresh();
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2000);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={onSave} className="space-y-6 max-w-3xl">
      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
          <Shield className="w-4 h-4 mr-2 text-primary-600" />
          Politique de mot de passe
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Longueur minimale">
            <input
              type="number"
              min={6}
              max={128}
              value={minLength}
              onChange={(e) => setMinLength(Number(e.target.value))}
              className={inputClass}
            />
          </Field>
          <Field label="Historique (N derniers mdp interdits)" hint="0 pour désactiver">
            <input
              type="number"
              min={0}
              max={50}
              value={historyCount}
              onChange={(e) => setHistoryCount(Number(e.target.value))}
              className={inputClass}
            />
          </Field>
          <Field label="Expiration (jours)" hint="0 = pas d'expiration">
            <input
              type="number"
              min={0}
              max={3650}
              value={maxAgeDays}
              onChange={(e) => setMaxAgeDays(Number(e.target.value))}
              className={inputClass}
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
          <Toggle label="Majuscule" checked={requireUpper} onChange={setRequireUpper} />
          <Toggle label="Minuscule" checked={requireLower} onChange={setRequireLower} />
          <Toggle label="Chiffre" checked={requireDigit} onChange={setRequireDigit} />
          <Toggle label="Caractère spécial" checked={requireSpecial} onChange={setRequireSpecial} />
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Politique de session</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Inactivité avant expiration (minutes)">
            <input
              type="number"
              min={5}
              max={43200}
              value={idleMinutes}
              onChange={(e) => setIdleMinutes(Number(e.target.value))}
              className={inputClass}
            />
          </Field>
          <Field label="Durée max d'une session (heures)">
            <input
              type="number"
              min={1}
              max={720}
              value={maxSessionHours}
              onChange={(e) => setMaxSessionHours(Number(e.target.value))}
              className={inputClass}
            />
          </Field>
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-gray-900 mb-3">Politique de verrouillage</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Échecs avant verrouillage">
            <input
              type="number"
              min={3}
              max={20}
              value={maxAttempts}
              onChange={(e) => setMaxAttempts(Number(e.target.value))}
              className={inputClass}
            />
          </Field>
          <Field label="Durée du verrouillage (minutes)">
            <input
              type="number"
              min={1}
              max={1440}
              value={lockoutMinutes}
              onChange={(e) => setLockoutMinutes(Number(e.target.value))}
              className={inputClass}
            />
          </Field>
        </div>
        <div className="mt-2">
          <Toggle
            label="Notifier les administrateurs lors d'un verrouillage"
            checked={notifyAdmins}
            onChange={setNotifyAdmins}
          />
        </div>
      </section>

      <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
        {savedAt && (
          <span className="inline-flex items-center text-sm text-green-600">
            <CheckCircle2 className="w-4 h-4 mr-1" />
            Enregistré
          </span>
        )}
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
        >
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Enregistrer
        </button>
      </div>
    </form>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange(v: boolean): void;
}) {
  return (
    <label className="flex items-center cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
      />
      <span className="ml-2 text-sm text-gray-700">{label}</span>
    </label>
  );
}
